/**
 * QuikSkill — "AI-first learning & skills management" page.
 * All copy lives here; sections only handle layout and motion.
 */

import { LINKS } from "./quikit";

export const QSK = {
  meta: {
    title: "AI-first LMS & skills management — QuikSkill by Quikit",
    description:
      "QuikSkill is an AI-first LMS that connects courses, learning paths, assessments, skills, certifications and employee development in one intelligent platform.",
  },
  links: { register: LINKS.register },
};

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */
export const QSK_HERO = {
  eyebrow: "AI-first learning & skills management",
  title: "Don’t just train\nyour people.\nBuild their skills.",
  sub: "QuikSkill is an AI-first LMS that connects courses, learning paths, assessments, skills, certifications and employee development in one intelligent platform.",
  lead: "Help every employee learn what matters, close critical skill gaps and build the capabilities your business needs to grow.",
  foot: "Start free. No unnecessary complexity.",
  link: { label: "See QuikSkill in action", href: "#dashboard" },
};

/** The capability snapshot shown in the hero. */
export const QSK_PULSE = {
  path: "Workspace → Skills → Engineering",
  coverage: { label: "Skill coverage", value: "64% · Developing", meta: "18 roles mapped" },
  certs: { label: "Certifications", value: "47 current", meta: "6 expiring in 30 days" },
  rows: [
    { name: "Cloud architecture", metricLabel: "Critical gap", metric: 34, state: "down" as const },
    { name: "Secure code review", metricLabel: "Developing", metric: 58, state: "up" as const },
    { name: "Data modelling", metricLabel: "On track", metric: 81, state: "up" as const },
    { name: "Incident response", metricLabel: "Critical gap", metric: 29, state: "down" as const },
  ],
  closed: { done: 12, total: 31, note: "9 paths assigned" },
  footer: "Cloud architecture is the gap blocking 3 roles from readiness.",
};

/* ------------------------------------------------------------------ */
/*  The problem                                                        */
/* ------------------------------------------------------------------ */
export const QSK_PROBLEM = {
  eyebrow: "The problem",
  // Too long for the column on one line, so the break is placed rather than left to chance.
  title: "Your LMS is tracking learning.\nBut is it building capability?",
  text: "Most LMS platforms tell you who enrolled, completed and passed. They don’t answer the bigger question: are people building the skills you need?",
  items: [
    {
      title: "Too many courses, too little direction",
      text: "Employees can have access to hundreds of courses but still not know what is relevant to their role, skills or career growth.",
    },
    {
      title: "Completion doesn’t mean capability",
      text: "Completing a course doesn’t automatically mean someone can apply what they learned or perform better.",
    },
    {
      title: "Skill gaps stay hidden",
      text: "Organizations can see training completion, but often lack a clear view of which skills are missing across employees, teams and roles.",
    },
    {
      title: "Everyone gets the same learning",
      text: "Different people have different roles, experience levels and development needs. One learning path rarely works for everyone.",
    },
    {
      title: "Learning is disconnected from performance",
      text: "Training often exists separately from employee goals, KPIs, performance reviews and development plans.",
    },
    {
      title: "L&D spends its time administering",
      text: "Assignments, reminders, assessments, certifications, reporting and follow-ups consume time that could build better programs.",
    },
  ],
  result: {
    kicker: "The real problem",
    title: "The problem isn’t a lack of training. It’s the gap between learning and capability.",
    text: "Completion is an activity measure. Capability is the business outcome.",
    cta: "See how QuikSkill works",
  },
};

/* ------------------------------------------------------------------ */
/*  The shift                                                          */
/* ------------------------------------------------------------------ */
export const QSK_SHIFT = {
  eyebrow: "The shift",
  title: "Stop tracking course completion. Start building skills.",
  text: "Traditional LMS platforms follow one model: assign, learn, complete, report. QuikSkill moves learning toward a continuous capability-building system.",
  /** Each row is a genuine swap — no term appears on both sides of the arrow. */
  rows: [
    { from: "Assign", to: "Discover" },
    { from: "Complete", to: "Assess" },
    { from: "One path for all", to: "Personalize" },
    { from: "Chase completion", to: "Develop" },
    { from: "Report", to: "Measure" },
    { from: "Course completion", to: "Capability" },
  ],
  questionsLead: "Instead of asking “how many courses did our employees complete?”, QuikSkill helps you ask the questions that decide whether your workforce is ready:",
  questions: [
    "What skills do we need?",
    "Where are the gaps?",
    "What should each person learn next?",
    "Are they developing?",
    "Is learning connected to performance?",
  ],
};

/* ------------------------------------------------------------------ */
/*  How it works                                                       */
/* ------------------------------------------------------------------ */
export const QSK_STEPS = {
  eyebrow: "How QuikSkill works",
  title: "One space. The complete learning and skills journey.",
  text: "QuikSkill connects learning, skills, assessments and employee development into one continuous journey.",
  steps: [
    {
      kicker: "Discover",
      title: "The skills your roles require",
      text: "Understand the skills and competencies required across roles, teams and functions.",
    },
    {
      kicker: "Assess",
      title: "Where your people stand today",
      text: "Measure existing knowledge and capabilities through assessments and knowledge checks.",
    },
    {
      kicker: "Learn",
      title: "Courses built around the business",
      text: "Create and deliver courses, learning paths, videos and assessments around roles, skills and business needs.",
    },
    {
      kicker: "Personalize",
      title: "The right learning per person",
      text: "Use AI to recommend relevant learning based on each employee’s role, skills, assessments and progress.",
    },
    {
      kicker: "Develop",
      title: "Growth beyond the course",
      text: "Connect learning with skills, development plans, performance and goals so learning continues beyond course completion.",
    },
    {
      kicker: "Measure",
      title: "Capability, not completion",
      text: "Track learning progress, certifications, assessments and skills to understand how workforce capability is evolving.",
    },
  ],
  cta: "Start free",
};

/* ------------------------------------------------------------------ */
/*  Learning strategy                                                  */
/* ------------------------------------------------------------------ */
export const QSK_STRATEGY = {
  eyebrow: "Your learning strategy",
  title: "Give every employee a clearer path to growth.",
  text: "Learning shouldn’t begin with a catalogue of courses. It should begin with understanding what your business, roles and people actually need.",
  cards: [
    { name: "Your business", text: "What capabilities does the organization need to achieve its goals?" },
    { name: "Your roles", text: "What skills and competencies does each role require?" },
    { name: "Your people", text: "What skills does each employee already have?" },
    { name: "Your gaps", text: "Where does actual capability differ from what the role requires?" },
    { name: "Your learning", text: "What training, content and development activities can close those gaps?" },
    { name: "Your goals", text: "How does development connect with performance and business objectives?" },
  ],
  close: "From learning activity to workforce capability.",
};

/* ------------------------------------------------------------------ */
/*  Dashboard + analytics                                              */
/* ------------------------------------------------------------------ */
export const QSK_DASHBOARD = {
  eyebrow: "Learning dashboard",
  title: "See your learning operation in one view.",
  text: "Stop piecing together LMS reports, spreadsheets and HR data. QuikSkill gives HR and L&D visibility across the learning lifecycle.",
  cards: [
    { icon: "chart", title: "Learning progress", text: "See enrollment, progress and completion across the organization." },
    { icon: "check", title: "Assessment performance", text: "Understand knowledge and assessment outcomes." },
    { icon: "target", title: "Skills", text: "Track skills and competencies across employees and roles." },
    { icon: "shield", title: "Certifications", text: "Monitor credentials, certification status and renewals." },
    { icon: "bolt", title: "Skill gaps", text: "Identify where capability needs development." },
    { icon: "grid", title: "Learning trends", text: "Understand how learning is progressing across the organization." },
  ],
  questionsTitle: "Go beyond completion rates.",
  questionsLead: "A completion percentage doesn’t tell you whether your learning strategy is working. QuikSkill moves the reporting question on:",
  questions: ["Who is developing?", "Where are the gaps?", "What learning is working?", "What needs attention?", "How is capability changing?"],
};

/* ------------------------------------------------------------------ */
/*  Courses and learning paths                                         */
/* ------------------------------------------------------------------ */
export const QSK_BUILD = {
  eyebrow: "Courses & learning paths",
  title: "Turn individual courses into guided learning journeys.",
  text: "A course can teach one topic. A learning path can build a capability. QuikSkill gives you one centralized library and the structure to turn it into a journey.",
  blocks: [
    {
      kicker: "Course management",
      title: "Create learning that works for your organization",
      text: "A centralized course environment where learning is structured around subjects, roles, skills, competencies, organizational requirements and development goals.",
      items: ["Create", "Structure", "Assign", "Track", "Improve"],
      chain: true,
    },
    {
      kicker: "Learning paths",
      title: "Build a path toward capability",
      text: "Structured journeys that combine courses, assessments, learning activities, skills and development requirements into something that builds a capability.",
      items: ["Role-based", "Skill-based", "Career-based", "Business-based"],
    },
  ],
  close: "One centralized learning library. Built around your business.",
};

/* ------------------------------------------------------------------ */
/*  Skills and gap analysis                                            */
/* ------------------------------------------------------------------ */
export const QSK_SKILLS = {
  eyebrow: "Skills & competencies",
  title: "Know the skills your business has — and the ones it needs.",
  text: "Skills bridge learning and capability. Define the competencies you need, connect them to roles, then make the gaps visible.",
  blocks: [
    {
      kicker: "Skill architecture",
      title: "Connect people, roles and learning",
      text: "Define organizational skills and competencies and connect them with employees, roles and learning requirements.",
      items: ["Define", "Map", "Assess", "Develop", "Track"],
      chain: true,
    },
    {
      kicker: "Skill gap analysis",
      title: "Make invisible skill gaps visible",
      text: "Compare employee capabilities against the skills required for roles and organizational needs, then turn what’s missing into a development plan.",
      items: ["Identify", "Prioritize", "Recommend", "Develop", "Track"],
      chain: true,
    },
  ],
  close: "Turn skill gaps into development plans.",
};

/* ------------------------------------------------------------------ */
/*  Assessments                                                        */
/* ------------------------------------------------------------------ */
export const QSK_ASSESS = {
  eyebrow: "Assessments",
  title: "Don’t assume learning happened. Measure it.",
  text: "Training without measurement is guesswork. QuikSkill adds assessments so you can see learning become capability.",
  steps: [
    { name: "Pre-assess", text: "Understand where learners start." },
    { name: "Assess", text: "Measure knowledge during or after learning." },
    { name: "Validate", text: "Confirm whether learning objectives have been achieved." },
    { name: "Identify gaps", text: "Find areas where additional development may be required." },
    { name: "Improve", text: "Use assessment insights to refine learning journeys." },
  ],
  proctor: {
    kicker: "AI-proctored assessments",
    title: "Make assessments more meaningful",
    text: "Assessment integrity matters when results are used to validate knowledge, certify employees or understand workforce capability. QuikSkill supports AI-assisted proctoring to strengthen assessment integrity.",
    items: ["Assess", "Monitor", "Validate", "Act"],
    close: "Better assessments lead to better capability decisions.",
  },
};

/* ------------------------------------------------------------------ */
/*  Certifications and development                                     */
/* ------------------------------------------------------------------ */
export const QSK_BEYOND = {
  eyebrow: "Beyond the course",
  title: "Learning doesn’t end when the course is complete.",
  text: "Certifications need tracking long after a course closes, and real development happens over time. QuikSkill keeps both inside the learning environment.",
  blocks: [
    {
      kicker: "Certifications",
      title: "Keep certifications from becoming a spreadsheet problem",
      text: "Certifications can be critical for compliance, customer requirements, professional development and role readiness.",
      items: ["Track credentials", "Monitor expiry", "Follow up", "Maintain visibility", "Connect with learning"],
    },
    {
      kicker: "Employee development",
      title: "From training event to continuous growth",
      text: "QuikSkill connects learning with development plans, skills, goals and performance so learning becomes part of an ongoing growth journey.",
      items: ["Development plans", "Skill development", "Goals", "Performance", "Progress"],
    },
  ],
  close: "Know who is certified, what’s expiring and what needs attention.",
};

/* ------------------------------------------------------------------ */
/*  AI layer                                                           */
/* ------------------------------------------------------------------ */
export const QSK_AI = {
  eyebrow: "AI learning intelligence",
  title: "Stop making employees search for what to learn next.",
  text: "A traditional LMS asks which course someone should complete. An AI-first platform asks what that person needs to develop next.",
  recommend: {
    kicker: "AI recommendations",
    title: "The right learning, at the right time, for the right person",
    text: "QuikSkill uses AI to help employees discover relevant learning based on role, skills, assessments, learning history, development needs and progress.",
    items: ["Role", "Skills", "Assessments", "Learning history", "Development needs", "Progress"],
    close: "Less searching. More relevant learning.",
  },
  intelligence: {
    kicker: "Inside the learning experience",
    title: "AI that understands what people need to learn",
    text: "AI shouldn’t be another tool your L&D team has to manage. It should be part of how people learn and how L&D teams make decisions.",
    items: [
      { name: "Recommend", text: "Suggest relevant learning based on individual context." },
      { name: "Personalize", text: "Adapt learning to each person’s role and journey." },
      { name: "Identify", text: "Surface potential skill gaps and development needs." },
      { name: "Summarize", text: "Make information easier to understand and consume." },
      { name: "Guide", text: "Help employees and L&D teams decide what comes next." },
    ],
    close: "AI makes learning more relevant — not simply more automated.",
  },
  cta: "Start free",
};

/* ------------------------------------------------------------------ */
/*  Roles                                                              */
/* ------------------------------------------------------------------ */
export type RoleKey = "ceo" | "hr" | "manager" | "employee";
export const QSK_ROLES = {
  eyebrow: "Built for everyone in the loop",
  title: "One platform, four very different questions.",
  text: "Leadership asks if the workforce is ready. Managers ask where teams need support. Employees ask what to learn next.",
  tabs: [
    { key: "ceo" as RoleKey, label: "Founders & CEOs" },
    { key: "hr" as RoleKey, label: "HR & L&D" },
    { key: "manager" as RoleKey, label: "Managers" },
    { key: "employee" as RoleKey, label: "Employees" },
  ],
  panels: {
    ceo: {
      eyebrow: "For founders & CEOs",
      title: "Know if your workforce has the skills to grow.",
      text: "Growth creates new roles, responsibilities and capability requirements. The question isn’t how much training you’re delivering — it’s whether you have the skills required for the next stage.",
      items: [
        "Workforce capability across the organization",
        "Skill gaps that need development",
        "Readiness for critical roles",
        "Certification coverage and qualifications",
        "Learning investment connected to employee growth",
        "Learning aligned with the capabilities the business needs",
      ],
      cta: "See workforce capability",
    },
    hr: {
      eyebrow: "For HR & L&D",
      title: "Spend less time managing learning. More time developing people.",
      text: "Your L&D team shouldn’t spend most of its time assigning courses, chasing completion and preparing reports. QuikSkill brings the learning operation into one workspace.",
      items: [
        "One platform for courses, paths, assessments, skills and certifications",
        "Less repetitive learning administration",
        "AI-recommended learning instead of manual curation",
        "Clearer skill visibility across employees, teams and roles",
        "Reporting on progress and workforce capability",
        "Development connected to performance and goals",
      ],
      cta: "Build your learning program",
    },
    manager: {
      eyebrow: "For managers",
      title: "A clearer view of your team’s development.",
      text: "Managers shouldn’t have to wait for an annual review to understand where their people need support. QuikSkill connects role, skills, learning, development and performance.",
      items: [
        "Know the skills your team’s roles require",
        "See the gaps that need development",
        "Recommend relevant learning to your people",
        "Track how development is progressing",
        "Use learning insights in development conversations",
        "Become an active partner in employee growth",
      ],
      cta: "See it for my team",
    },
    employee: {
      eyebrow: "For employees",
      title: "Know what to learn next.",
      text: "Don’t search through hundreds of courses. Your learning experience should be personal to your role, your current capability and where you’re going.",
      items: [
        "What you need — skills relevant to your role",
        "Where you stand — capability through assessments and progress",
        "What to learn — relevant courses and recommendations",
        "What’s next — structured learning and development paths",
        "Where you’re going — learning tied to goals and career growth",
        "Your role. Your skills. Your learning journey.",
      ],
      cta: "Start learning free",
    },
  },
};

/* ------------------------------------------------------------------ */
/*  Why QuikSkill                                                      */
/* ------------------------------------------------------------------ */
export const QSK_WHY = {
  eyebrow: "Why QuikSkill",
  title: "We didn’t build another course library.",
  text: "We built a capability-building platform. Less course administration. More capability building.",
  items: [
    {
      title: "LMS fundamentals built in",
      text: "Courses, learning paths, content, assessments, certifications, progress tracking and analytics provide the foundation of a modern LMS.",
    },
    {
      title: "Skills at the core",
      text: "Connect courses with skills, roles and competencies so learning has a purpose beyond completion.",
    },
    {
      title: "AI makes learning personal",
      text: "AI helps employees discover relevant learning based on their roles, skills, assessments and development needs.",
    },
    {
      title: "Skill gaps become visible",
      text: "Understand where capability gaps exist across employees, teams and roles.",
    },
    {
      title: "Learning connects with performance",
      text: "Connect with QuikPeople and QuikScale to bring learning, development, performance and goals into one ecosystem.",
    },
    {
      title: "Learning becomes continuous",
      text: "Employees don’t stop developing after completing a course — development carries on through plans, skills and goals.",
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  Compare                                                            */
/* ------------------------------------------------------------------ */
export type CompareCell = boolean | string;
export const QSK_COMPARE = {
  eyebrow: "QuikSkill vs the old way",
  title: "You don’t need another course library. You need a capability platform.",
  text: "Capability by capability, against a traditional LMS and against stitching a separate learning stack together.",
  columns: ["QuikSkill", "Traditional LMS", "Separate stack"],
  rows: [
    { feature: "Course management", cells: [true, true, true] as CompareCell[] },
    { feature: "Learning paths", cells: [true, true, "Varies"] as CompareCell[] },
    { feature: "Assessments", cells: [true, true, "Varies"] as CompareCell[] },
    { feature: "Certifications", cells: [true, true, "Separate tools"] as CompareCell[] },
    { feature: "Learning analytics", cells: [true, true, "Multiple systems"] as CompareCell[] },
    { feature: "Skills management", cells: [true, "Varies", "Separate system"] as CompareCell[] },
    { feature: "Skill gap analysis", cells: [true, "Varies", "Separate system"] as CompareCell[] },
    { feature: "AI learning assistance", cells: ["AI-first", "Varies", "Add-on"] as CompareCell[] },
    { feature: "AI recommendations", cells: ["Built in", "Varies", "Add-on"] as CompareCell[] },
    { feature: "Employee development", cells: ["Connected", "Often separate", "HRMS / separate"] as CompareCell[] },
    { feature: "People & HR context", cells: ["QuikPeople", "Integration", "Separate"] as CompareCell[] },
    { feature: "Goals & KPIs", cells: ["QuikScale", "Integration", "Separate"] as CompareCell[] },
    { feature: "Learning + projects", cells: ["QuikProjects", "Integration", "Separate"] as CompareCell[] },
    { feature: "Personalized learning", cells: [true, "Varies", "Multiple systems"] as CompareCell[] },
    { feature: "Capability visibility", cells: [true, "Limited", "Fragmented"] as CompareCell[] },
  ],
  close: {
    title: "Three ways to run learning.",
    options: [
      { name: "Traditional LMS", text: "Helps you manage learning — courses, progress and completion reports." },
      { name: "A separate learning stack", text: "Helps you manage pieces of the operation, each in its own system." },
    ],
    product: { name: "QuikSkill", text: "Connects learning, skills and development into one capability-building platform." },
  },
};

/* ------------------------------------------------------------------ */
/*  The business case                                                  */
/* ------------------------------------------------------------------ */
export const QSK_CASE = {
  eyebrow: "The business case",
  title: "Your LMS subscription isn’t the only cost.",
  text: "The real cost is more than the licence. It’s managing courses, assigning training, chasing completion and reporting.",
  manual: {
    kicker: "The typical learning stack",
    title: "Multiple tools, plus the administration around them",
    items: [
      "Manual administration",
      "Course overload",
      "Low relevance",
      "Manual reporting",
      "Hidden skill gaps",
      "Disconnected performance",
      "Limited personalization",
      "Multiple tools",
    ],
    value: "Multiple tools",
    unit: "+ administration",
  },
  product: {
    kicker: "QuikSkill",
    title: "One connected learning & skills platform",
    text: "Courses, learning paths, assessments, skills, certifications, employee development, analytics and AI-powered learning intelligence in one workspace.",
    close: "One learner. One skill profile. One platform.",
    value: "One",
    unit: "connected platform",
  },
  /** Product- and customer-reported figures, presented as such rather than as guarantees. */
  proof: {
    label: "Reported by QuikSkill customers",
    stats: [
      { value: "8 hrs", text: "saved per week on certification tracking" },
      { value: "3×", text: "faster course-to-certification time" },
      { value: "92%", text: "assessment-integrity confidence" },
    ],
    note: "Figures are product- and customer-reported examples, not guaranteed outcomes. Results depend on learning quality, employee engagement, practice and organizational support.",
  },
};

/* ------------------------------------------------------------------ */
/*  The ecosystem                                                      */
/* ------------------------------------------------------------------ */
export const QSK_ECOSYSTEM = {
  eyebrow: "Connected with Quikit",
  title: "Learning doesn’t exist in isolation.",
  text: "Learning becomes more powerful when it understands the person, their work and the goals they’re working toward.",
  groups: [
    { name: "QuikPeople", items: ["Employee profiles", "Development context", "Performance context"] },
    { name: "QuikScale", items: ["Goals and KPIs", "Business performance", "Strategic priorities"] },
    { name: "QuikProjects", items: ["The actual work", "Delivery context", "Capability in practice"] },
    { name: "QuikChat", items: ["Learning conversations", "Team collaboration", "Closer to the team"] },
  ],
  close: "One employee. One skill profile. One connected business context.",
};

/* ------------------------------------------------------------------ */
/*  Plans                                                              */
/* ------------------------------------------------------------------ */
/**
 * The brief prices only the trial and leaves both paid tiers as "Custom", and explicitly warns
 * against publishing an invented per-user number. Premium therefore uses the $1 / user / month
 * already published for QuikSkill in the product catalogue (`PRODUCTS` in quikit.ts). Pro's
 * figure is a PLACEHOLDER — not from the brief. Replace `PRO_PRICE` before publishing.
 */
const PRO_PRICE = "$1.99";

export const QSK_PLANS = {
  eyebrow: "Pricing",
  title: "A learning platform that scales with your workforce.",
  text: "Every plan starts with a free trial.",
  plans: [
    {
      name: "Free",
      price: "$0",
      unit: "trial",
      text: "Experience the full QuikSkill product before you commit.",
      lead: "",
      cta: "Start free",
      ctaHref: LINKS.register,
      foot: "No credit card required",
      features: [
        "Course management",
        "Learning paths",
        "Assessments",
        "Skills & competencies",
        "AI learning recommendations",
        "Skill gap analysis",
        "Certifications",
        "Employee development",
        "Learning analytics",
        "AI learning intelligence",
      ],
    },
    {
      name: "Premium",
      price: "$1",
      unit: "/ user / month",
      text: "For organizations building a connected learning and skills operation.",
      lead: "Everything in Free, plus:",
      featured: true,
      badge: "Most popular",
      cta: "Get QuikSkill",
      ctaHref: LINKS.register,
      foot: "Free trial included",
      features: [
        "Full LMS capabilities",
        "Learning paths",
        "Skills & competency management",
        "Assessments",
        "Skill gap analysis",
        "Certifications",
        "Employee development",
        "Learning analytics",
        "Support and onboarding",
      ],
    },
    {
      name: "Pro",
      price: PRO_PRICE,
      unit: "/ user / month",
      text: "For teams that want AI learning intelligence and the connected Quikit ecosystem.",
      lead: "Everything in Premium, plus:",
      cta: "Get QuikSkill Pro",
      ctaHref: LINKS.register,
      foot: "Free trial included",
      features: [
        "AI learning recommendations",
        "AI learning intelligence",
        "AI-proctored assessments",
        "Connected Quikit ecosystem",
        "Advanced learning analytics",
      ],
    },
    {
      name: "Enterprise",
      price: "Custom",
      unit: "",
      text: "For larger organizations and complex learning environments.",
      lead: "",
      cta: "Talk to sales",
      ctaHref: "#demo",
      foot: "Tailored to your organization",
      features: [
        "Enterprise deployment",
        "Dedicated account management",
        "Migration & onboarding",
        "Custom integrations",
        "Advanced permissions",
        "Priority support",
        "API and integration support",
      ],
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  FAQ                                                                */
/* ------------------------------------------------------------------ */
export const QSK_FAQ = [
  {
    q: "What is QuikSkill?",
    a: "QuikSkill is an AI-first LMS and skills management platform that combines courses, learning paths, assessments, certifications, skills, employee development and learning analytics with AI-powered learning intelligence.",
  },
  {
    q: "Is QuikSkill an LMS?",
    a: "Yes. QuikSkill provides the core capabilities of a modern LMS while adding skills management and an AI-first learning experience.",
  },
  {
    q: "Who is QuikSkill for?",
    a: "Organizations, HR teams, L&D leaders, managers and employees that want to move beyond course completion and build skills aligned with roles and business goals.",
  },
  {
    q: "Can we create and manage courses?",
    a: "Yes. QuikSkill provides course and content management capabilities for organizing and delivering learning programs.",
  },
  {
    q: "Can we create learning paths?",
    a: "Yes. Learning paths combine courses, assessments and development activities into structured journeys based on roles, skills or organizational requirements.",
  },
  {
    q: "Does QuikSkill support assessments?",
    a: "Yes. QuikSkill supports assessments and knowledge checks to measure progress, validate knowledge and identify areas needing development.",
  },
  {
    q: "Does QuikSkill manage skills?",
    a: "Yes. Skills and competency management lets organizations connect capabilities with employees, roles and learning requirements.",
  },
  {
    q: "Can QuikSkill identify skill gaps?",
    a: "Yes. QuikSkill compares employee capabilities with role or organizational requirements to identify areas that may require development.",
  },
  {
    q: "Does QuikSkill provide AI learning recommendations?",
    a: "Yes. QuikSkill uses AI to recommend relevant learning based on employee roles, skills, assessments and learning progress.",
  },
  {
    q: "Can employees get personalized learning?",
    a: "Yes. QuikSkill is designed to make learning relevant to individual roles, capabilities and development needs rather than giving everyone the same journey.",
  },
  {
    q: "Can managers see their team’s skills?",
    a: "Yes. Managers can access relevant skill, learning and development information based on their permissions and organizational setup.",
  },
  {
    q: "Does QuikSkill support certifications?",
    a: "Yes. Certification management lets organizations maintain visibility into employee credentials and learning achievements, including renewal-related tracking.",
  },
  {
    q: "Can learning connect with performance?",
    a: "Yes. QuikSkill connects with QuikPeople and QuikScale so learning, development, performance, goals and skills work together.",
  },
  {
    q: "Does QuikSkill support AI-proctored assessments?",
    a: "Yes. AI-assisted proctoring is part of the product experience, supporting assessment integrity where results validate knowledge or certify employees.",
  },
  {
    q: "Can QuikSkill work as a standalone LMS?",
    a: "Yes. QuikSkill functions as an LMS and skills platform on its own. Connecting other Quikit applications adds context around people, performance, projects and goals.",
  },
  {
    q: "Do I need the complete Quikit suite?",
    a: "No. QuikSkill works as a standalone learning and skills platform. The wider Quikit ecosystem adds connected context when required.",
  },
  {
    q: "How is QuikSkill different from a traditional LMS?",
    a: "A traditional LMS primarily manages courses, progress and completion. QuikSkill adds skills management, skill-gap analysis, AI recommendations, employee development and connections with performance and business goals.",
  },
  {
    q: "Does QuikSkill replace an L&D team?",
    a: "No. QuikSkill reduces administrative work and gives L&D teams better intelligence and visibility so they can focus on learning strategy and employee development.",
  },
  {
    q: "Does QuikSkill guarantee skill improvement?",
    a: "No. QuikSkill provides the infrastructure, recommendations, assessments and capability insights to support development, but actual improvement depends on learning quality, employee engagement, practice and organizational support.",
  },
];

/* ------------------------------------------------------------------ */
/*  Demo                                                               */
/* ------------------------------------------------------------------ */
export type QskIntent = "replace" | "skills" | "evaluate";
export const QSK_DEMO = {
  agenda: [
    { title: "Your roles, mapped to skills", text: "What capability each role in your organization actually requires." },
    { title: "The gaps we’d surface", text: "Where current capability differs from what those roles need." },
    { title: "The learning that closes them", text: "How paths, assessments and AI recommendations get people there." },
  ],
  intents: [
    { value: "replace" as QskIntent, label: "Replace our LMS", meta: "We already have one" },
    { value: "skills" as QskIntent, label: "Build a skills layer", meta: "Map roles to capability" },
    { value: "evaluate" as QskIntent, label: "Just evaluating", meta: "Exploring options" },
  ],
  copy: {
    replace: {
      eyebrow: "Move off your current LMS",
      title: "Let’s plan the switch.",
      lead: "Tell us what your LMS handles today and how many people use it. We’ll show you what QuikSkill would take over.",
      button: "Plan my switch",
      ok: "Request received",
      msg: "A QuikSkill specialist will contact you to plan the switch.",
    },
    skills: {
      eyebrow: "Build your skills layer",
      title: "Start with the skills your roles need.",
      lead: "Tell us the roles you’re building for. We’ll show you how QuikSkill maps skills, finds gaps and turns them into learning paths.",
      button: "Map my roles",
      ok: "Request received",
      msg: "A QuikSkill specialist will contact you to map your roles and skills.",
    },
    evaluate: {
      eyebrow: "Evaluate QuikSkill",
      title: "Comparing learning platforms?",
      lead: "Tell us how learning runs today — an LMS, spreadsheets, or nothing formal yet. We’ll show you how QuikSkill would fit.",
      button: "Book my walkthrough",
      ok: "Request received",
      msg: "A QuikSkill specialist will contact you to set up your walkthrough.",
    },
  } satisfies Record<QskIntent, { eyebrow: string; title: string; lead: string; button: string; ok: string; msg: string }>,
  sizes: ["1–10", "11–50", "51–200", "201–500", "500+"],
  current: ["A traditional LMS", "An HRMS learning module", "Spreadsheets", "External training providers", "Ad-hoc courses", "Nothing formal yet"],
};

/* ------------------------------------------------------------------ */
/*  Closing                                                            */
/* ------------------------------------------------------------------ */
export const QSK_CTA = {
  eyebrow: "Stop tracking course completion",
  title: "Start building skills.",
  text: "Your people don’t need more courses. They need the right skills, the right path and a clear link between what they learn and what you need.",
  steps: ["Courses", "Learning paths", "Assessments", "Skills", "Skill gap analysis", "Certifications", "Development", "AI intelligence"],
  foot: "Your learning. Your people. Your skills. One intelligent platform.",
};
